import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useWorkspace } from '../../../../context/WorkspaceContext';
import { Button } from '../../../../ui/button';
import { FaPlus, FaSync } from 'react-icons/fa';
import MultiSelect from '../../../../ui/MultiSelect';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const PinterestSettings = React.memo(({ formData, handleInputChange, userSocials }) => {
  const { activeWorkspace, idToken } = useWorkspace();
  const [isLoading, setIsLoading] = useState(false);
  const [boards, setBoards] = useState([]);
  const [isCreatingBoard, setIsCreatingBoard] = useState(false);
  const [showCreateBoardModal, setShowCreateBoardModal] = useState(false);
  const [newBoardData, setNewBoardData] = useState({
    name: '',
    description: '',
    privacy: 'PUBLIC'
  });

  const getPinterestBoards = useCallback(async (platformId) => {
    if (!idToken || !activeWorkspace.id || !platformId) return;

    try {
      setIsLoading(true);
      const response = await axios.get(BACKEND_API_URL + '/api/pinterest/boards/list', {
        params: { 
          workspaceId: activeWorkspace.id,
          platform_id: platformId
        },
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (response.data && response.data.items) {
        setBoards(response.data.items);
      }
    } catch (error) {
      console.error('Error fetching Pinterest boards:', error);
      setBoards([]);
    } finally {
      setIsLoading(false);
    }
  }, [idToken, activeWorkspace.id]);

  const createNewBoard = useCallback(async (platformId) => {
    if (!idToken || !activeWorkspace.id || !platformId || !newBoardData.name.trim()) return;

    try {
      setIsCreatingBoard(true);
      const response = await axios.post(BACKEND_API_URL + '/api/pinterest/boards/create', {
        workspaceId: activeWorkspace.id,
        platform_id: platformId,
        name: newBoardData.name.trim(),
        description: newBoardData.description.trim(),
        privacy: newBoardData.privacy
      }, {
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (response.data) {
        // Refresh boards list
        await getPinterestBoards(platformId);
        // Set the newly created board as selected
        const currentBoards = formData.board_ids || [];
        handleInputChange('board_ids', [...currentBoards, response.data.id]);
        // Reset form and close modal
        setNewBoardData({ name: '', description: '', privacy: 'PUBLIC' });
        setShowCreateBoardModal(false);
      }
    } catch (error) {
      console.error('Error creating Pinterest board:', error);
      alert('Failed to create board. Please try again.');
    } finally {
      setIsCreatingBoard(false);
    }
  }, [idToken, activeWorkspace.id, newBoardData, getPinterestBoards, handleInputChange]);

  useEffect(() => {
    // Find the Pinterest platform ID for the current user
    const pinterestAccount = userSocials.find(social => social.platform === 'pinterest');
    if (pinterestAccount && pinterestAccount.platform_id) {
      getPinterestBoards(pinterestAccount.platform_id);
    }
  }, [userSocials, getPinterestBoards]);

  const handleCreateBoard = () => {
    const pinterestAccount = userSocials.find(social => social.platform === 'pinterest');
    if (pinterestAccount && pinterestAccount.platform_id) {
      createNewBoard(pinterestAccount.platform_id);
    }
  };

  const handleRefreshBoards = () => {
    const pinterestAccount = userSocials.find(social => social.platform === 'pinterest');
    if (pinterestAccount && pinterestAccount.platform_id) {
      getPinterestBoards(pinterestAccount.platform_id);
    }
  };

  return (
    <>
      <div className='flex flex-col py-3 --theme-colors rounded-lg px-2'>
        <div className='flex justify-between items-center'>
          <p className='font-semibold mr-3'>Pinterest Settings:</p>
          <div className='flex gap-2'>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshBoards}
              disabled={isLoading}
            >
              <FaSync size={10} className={isLoading ? 'animate-spin' : ''} />
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowCreateBoardModal(true)}
            >
              <FaPlus size={10} />
              New Board
            </Button>
          </div>
        </div>

        <div className='mt-3'>
          <label className="flex flex-col font-semibold justify-start items-start w-full text-sm font-medium text-gray-900">
            Boards:
            <div className="w-full mt-1">
              <MultiSelect
                options={boards}
                selectedValues={formData.board_ids || []}
                onChange={(newSelection) => handleInputChange('board_ids', newSelection)}
                placeholder={isLoading ? "Loading boards..." : "Click to select boards"}
                disabled={isLoading}
                renderOption={(board) => (
                  <div className="flex items-center justify-between w-full">
                    <div>
                      <div className="font-medium">{board.name}</div>
                      <div className="text-sm text-gray-500">
                        {board.privacy === 'SECRET' ? 'Private Board' : 'Public Board'}
                      </div>
                    </div>
                    {board.privacy === 'SECRET' && (
                      <span className="text-lg">🔒</span>
                    )}
                  </div>
                )}
                getValue={(board) => board.id}
                getDisplayName={(boardId) => {
                  const board = boards.find(b => b.id === boardId);
                  return board ? board.name : boardId;
                }}
                maxHeight="300px"
              />
            </div>
            {!isLoading && boards.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">No boards found. Create a new board to get started.</p>
            )}
          </label>
        </div>

        {/* Additional Pinterest-specific settings */}
        <div className='mt-3'>
          <label className="flex flex-col font-semibold justify-start items-start w-full text-sm font-medium text-gray-900">
            Link (optional):
            <input
              type="url"
              value={formData.link || ''}
              onChange={(e) => handleInputChange('link', e.target.value)}
              placeholder="https://example.com"
              className="form-input w-full rounded-lg border-gray-300 --theme-colors sm:text-sm mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Add a destination link for your pin</p>
          </label>
        </div>

        <div className='mt-3'>
          <label className="flex flex-col font-semibold justify-start items-start w-full text-sm font-medium text-gray-900">
            Alt Text (optional):
            <input
              type="text"
              value={formData.alt_text || ''}
              onChange={(e) => handleInputChange('alt_text', e.target.value)}
              placeholder="Describe your image for accessibility"
              maxLength={500}
              className="form-input w-full rounded-lg border-gray-300 --theme-colors sm:text-sm mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Helps make your pins accessible to everyone</p>
          </label>
        </div>
      </div>

      {/* Create Board Modal */}
      {showCreateBoardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Create New Pinterest Board</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Board Name *
                </label>
                <input
                  type="text"
                  value={newBoardData.name}
                  onChange={(e) => setNewBoardData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter board name"
                  className="w-full rounded-lg border p-2 border-gray-300 --theme-colors sm:text-sm"
                  maxLength={100}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={newBoardData.description}
                  onChange={(e) => setNewBoardData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your board"
                  rows={3}
                  className="w-full rounded-lg p-2 border border-gray-300 --theme-colors sm:text-sm"
                  maxLength={500}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Privacy
                </label>
                <select
                  value={newBoardData.privacy}
                  onChange={(e) => setNewBoardData(prev => ({ ...prev, privacy: e.target.value }))}
                  className="w-full rounded-lg p-2 border border-gray-300 --theme-colors sm:text-sm"
                >
                  <option value="PUBLIC">Public</option>
                  <option value="SECRET">Secret</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowCreateBoardModal(false);
                  setNewBoardData({ name: '', description: '', privacy: 'PUBLIC' });
                }}
                disabled={isCreatingBoard}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleCreateBoard}
                disabled={isCreatingBoard || !newBoardData.name.trim()}
              >
                {isCreatingBoard ? 'Creating...' : 'Create Board'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default PinterestSettings; 